import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Mail, Key, Send, CheckCircle, AlertCircle, Info } from 'lucide-react';
import api from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';

export default function SystemResendConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [config, setConfig] = useState({
    resend_api_key: '',
    resend_email_from: '',
    resend_domain: ''
  });
  const [existingConfig, setExistingConfig] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/system/resend-config');
      
      if (response.data.is_configured !== false) {
        setIsConfigured(true);
        setExistingConfig(response.data);
        // No mostramos la API key existente por seguridad
        setConfig({
          resend_api_key: '',
          resend_email_from: response.data.resend_email_from || '',
          resend_domain: response.data.resend_domain || ''
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching config:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validaciones
    if (!config.resend_api_key && !isConfigured) {
      toast.error('La API Key de Resend es requerida');
      return;
    }

    if (!config.resend_email_from) {
      toast.error('El email remitente es requerido');
      return;
    }

    // Validar formato de API key si se proporciona
    if (config.resend_api_key && !config.resend_api_key.startsWith('re_')) {
      toast.error('La API Key de Resend debe comenzar con "re_"');
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(config.resend_email_from)) {
      toast.error('Email remitente inválido');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        resend_email_from: config.resend_email_from,
        resend_domain: config.resend_domain || null
      };

      // Solo incluir API key si se proporcionó una nueva
      if (config.resend_api_key) {
        payload.resend_api_key = config.resend_api_key;
      }

      await api.post('/system/resend-config', payload);

      toast.success('Configuración de Resend guardada exitosamente');
      setIsConfigured(true);
      
      // Limpiar API key del formulario
      setConfig({ ...config, resend_api_key: '' });
      
      // Recargar configuración
      fetchConfig();
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!isConfigured) {
      toast.error('Primero debes guardar la configuración antes de probar');
      return;
    }

    try {
      setTesting(true);
      const response = await api.post('/system/resend-config/test');
      toast.success(response.data.message || 'Email de prueba enviado');
    } catch (error) {
      console.error('Error testing config:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar email de prueba');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2537b0]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Configuración Global de Resend</h1>
        <p className="text-sm text-slate-600 mt-1">
          Configuración de Resend para emails del sistema (verificación, recuperación de contraseña, etc.)
        </p>
      </div>

      {/* Info Alert */}
      <Alert className="mb-6 bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Importante:</strong> Esta configuración se usa para enviar emails de verificación de cuenta, 
          recuperación de contraseña y otros emails del sistema. Es diferente de la configuración individual 
          de cada usuario para campañas de marketing.
        </AlertDescription>
      </Alert>

      {/* Status */}
      {isConfigured && existingConfig && (
        <Alert className="mb-6 bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            <strong>Resend está configurado</strong>
            <div className="mt-2 text-sm">
              <p>• Email remitente: <strong>{existingConfig.resend_email_from}</strong></p>
              <p>• API Key: <strong>{existingConfig.resend_api_key_preview}</strong></p>
              <p>• Configurado por: {existingConfig.configured_by || 'N/A'}</p>
              <p>• Fecha: {new Date(existingConfig.configured_at).toLocaleString('es-MX')}</p>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {!isConfigured && (
        <Alert className="mb-6 bg-amber-50 border-amber-200">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Resend no está configurado.</strong> Los emails de verificación y recuperación de contraseña no funcionarán hasta que lo configures.
          </AlertDescription>
        </Alert>
      )}

      {/* Configuration Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Configuración de Resend
          </CardTitle>
          <CardDescription>
            Configura tu API key de Resend y el email remitente
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              API Key de Resend *
            </label>
            <Input
              type="password"
              value={config.resend_api_key}
              onChange={(e) => setConfig({ ...config, resend_api_key: e.target.value })}
              placeholder={isConfigured ? "Dejar vacío para mantener la actual" : "re_xxxxxxxxxxxxx"}
              className="font-mono text-sm"
            />
            <p className="text-xs text-slate-500 mt-1">
              {isConfigured 
                ? 'Deja este campo vacío si no deseas cambiar la API Key actual'
                : 'Obtén tu API key en: https://resend.com/api-keys'
              }
            </p>
          </div>

          {/* Email From */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Email Remitente *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="email"
                value={config.resend_email_from}
                onChange={(e) => setConfig({ ...config, resend_email_from: e.target.value })}
                placeholder="noreply@tudominio.com"
                className="pl-10"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Este email debe estar verificado en tu cuenta de Resend
            </p>
          </div>

          {/* Domain (Optional) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Dominio (Opcional)
            </label>
            <Input
              type="text"
              value={config.resend_domain}
              onChange={(e) => setConfig({ ...config, resend_domain: e.target.value })}
              placeholder="tudominio.com"
            />
            <p className="text-xs text-slate-500 mt-1">
              Opcional: especifica tu dominio si tienes uno configurado en Resend
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Guardar Configuración
                </>
              )}
            </Button>

            {isConfigured && (
              <Button
                onClick={handleTest}
                disabled={testing}
                variant="outline"
                className="flex items-center gap-2"
              >
                {testing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#2537b0]"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Enviar Email de Prueba
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Help Section */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-base">¿Cómo obtener tu API Key de Resend?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <ol className="list-decimal list-inside space-y-2">
            <li>Ve a <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-[#2537b0] hover:underline">resend.com</a> e inicia sesión</li>
            <li>Navega a "API Keys" en el menú lateral</li>
            <li>Haz click en "Create API Key"</li>
            <li>Dale un nombre (ej: "Asistelo Production") y selecciona permisos de envío</li>
            <li>Copia la API key que comienza con "re_" y pégala aquí</li>
            <li>Asegúrate de verificar tu dominio y email remitente en Resend</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
